import { sendAnalytics } from '@events/sendAnalytics';
import cookies from '@utils/cookies';
import { captureException } from '@utils/sentry';
import { GTMError } from '@utils/customErrors';
import { getBrowserInfo } from '@utils/browserInfo';
import segmentStorage from '@events/segmentStorage';
import uuid from '@utils/uuid';
import { evtNameCategoryMap } from '@constants/index';
import { analytics } from '../constants';

// anonymous token
const anonymousToken = process.env.ANONYMOUS_EVENTS_TOKEN;

const eventsUrl = process.env.EVENTS_URL;

// get browser name
const getBrowserName = (browserName) =>
  browserName === 'IE' ? 'Microsoft Internet Explorer' : browserName;

// get os name
const getOs = (osName) => {
  switch (true) {
    case /Mac OS/.test(osName):
      return 'MacOS';
    case /Windows/.test(osName):
      return 'Windows';
    case /Unix/.test(osName):
      return 'UNIX';
    case /Linux/.test(osName):
      return 'Linux';
    default:
      return 'Unknown OS';
  }
};

// defined cache variable
const info = new Map();

if (typeof window !== 'undefined') {
  const {
    browserName,
    osName,
    osVersion,
    deviceType,
    browserFullVersion
  } = getBrowserInfo(navigator.userAgent);
  info.set('os', getOs(osName));
  info.set('browser', getBrowserName(browserName));
  info.set('os_version', osVersion);
  info.set('app_version', Number(process.env.APP_VERSION));
  info.set('device', deviceType);
  info.set('browserVersion', browserFullVersion);
}

export const fetchAnalyticsSchema = async () => {
  const res = await fetch(
    `https://quark.uacdn.net/eventjson/webSchema.json?time=${new Date().getTime()}`
  );
  if (res.status === 200) {
    const data = await res.json();
    window.analyticsSchema = JSON.stringify(data);
  }
};

const analyticsAction = (entityName, itemId, data, parentInfo = {}) => {
  return { type: analytics.ANALYTICS, entityName, data, itemId, parentInfo };
};

export const analyticsApi = async (dataLoad, eventType = null) => {
  const data = dataLoad;
  data['Anonymous Session Id'] = cookies.readCookie('anonymous_session_id');
  const token = cookies.readCookie('jwtToken') ?? anonymousToken;
  let payload = null;
  const eventURL =
    eventType === 'downloadPDF' ? process.env?.CLX_PERF_SERVER : eventsUrl;
  if (!eventURL || !token) return;
  if (eventType === 'downloadPDF') {
    const playerId = uuid(); // format 60666f78_b805_4d34_a0aa_650f9c5b80a8
    const browserId = uuid(); // same format as above different value

    payload = [
      {
        name: 'Liveclass Events',
        version: 1,
        data: {
          os: info.get('os'),
          browser: info.get('browser'),
          client_timestamp: new Date().getTime(),
          ...data,
          Platform: window.isMobile ? 7 : 0,
          'App Version': Number(process.env.APP_VERSION),
          user_id: `${data?.me?.userID}`,
          class_id: '',
          is_educator: false,
          player_version: 0,
          device_type: 'Web',
          class_type: 'App',
          os_platform: '',
          device: '',
          player_id: playerId,
          browser_id: browserId,
          browser_name: info.get('browser'),
          browser_version: info.get('browserVersion'),
          evt_version: '4.3.0',
          application: 'LiveClass',
          ip: '',
          card: 1,
          measure1: 0,
          measure2: 0,
          measure3: 0,
          bandwidth: -1,
          network_type: 'na',
          network_rtt: -1,
          used_js_heap: -1,
          allocated_js_heap: -1,
          available_js_heap: -1,
          logical_core_count: -1,
          battery_charging: false,
          battery_time_to_charge: -1,
          battery_time_to_discharge: -1
        }
      }
    ];
  } else {
    payload = [
      {
        name: 'Android Events',
        version: 1,
        data: {
          os: info.get('os'),
          browser: info.get('browser'),
          client_timestamp: new Date().getTime(),
          ...data,
          Platform: window.isMobile ? 7 : 0,
          'App Version': Number(process.env.APP_VERSION)
        }
      }
    ];
  }
  try {
    if (eventType === 'downloadPDF' && !eventURL)
      throw new Error(
        `CLX url is not set issue. no name ${
          window.location.hostname
        } ${JSON.stringify(payload)}`
      );

    if (data.name)
      return await fetch(eventURL, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          authorization: `Bearer ${token}`,
          'Content-Type': 'application/json;charset=UTF-8',
          'X-Platfrom': window.isMobile ? 7 : 0
        }
      });
    throw new Error(
      `Analytics issue. no name ${window.location.hostname} ${JSON.stringify(
        payload
      )}`
    );
  } catch (error) {
    captureException(error, {}, 400);
  }
};

export const callAnalytics = (dataLoad = {}, trackers = {}) => {
  const data = dataLoad;
  if (window.isMobile) {
    data.Platform = 'Mobile web';
  }
  if (trackers.mixpanel && process.env.MIXPANEL_ID) {
    sendAnalytics(data.name, data);
  }

  if (window.webengage && trackers.mixpanel && process.env.WEBENGAGE_KEY)
    window.webengage.track(data.name, data);

  if (window.FS && trackers.mixpanel && process.env.FULLSTORY_ID)
    window.FS.event(data.name, data);

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push?.({
      event: data.name,
      ...data
    });
  } catch (e) {
    const error = new GTMError(
      `${e.message}; GTM dataLayer error.Event data:- ${JSON.stringify(data)}`
    );
    captureException(error);
  }

  analyticsApi(data);
};

const sendSegmentEvent = (eventName, payload) => {
  window?.analytics?.track(eventName, payload, {
    integrations: {
      'Google Analytics': {
        clientId: window?.gaGlobal?.vid
      }
    }
  });
};

/*
 func -> validationForSegmentEvents
 args -> payload ->    object containing data to be sent
                      to event server ,
         eventsKey ->  arrays of predefined keys to be tested
                      against the payload

*/
const validationForSegmentEvents = (payload, eventsKey) => {
  const payloadKeys = Object.keys(payload);
  const keysNotPresentInPayLoad = [];
  eventsKey.forEach((eventKey) => {
    const keyPresent = payloadKeys.find(
      (payLoadKey) => payLoadKey === eventKey
    );
    if (!keyPresent) {
      keysNotPresentInPayLoad.push({ [eventKey]: 'Key not present' });
    }
    if (
      keyPresent &&
      typeof payload[keyPresent] !== 'boolean' &&
      !payload[keyPresent]
    ) {
      keysNotPresentInPayLoad.push({
        [eventKey]: 'Key present, but data falsy except for boolean value '
      });
    }
  });
  // eslint-disable-next-line no-console
  console.log('Key not present', keysNotPresentInPayLoad);
};

export const segment = {
  track(
    eventName,
    properties = {},
    categoryStat = evtNameCategoryMap.WITHOUT_CATEGORY,
    validationCheck = false,
    eventsKey = []
  ) {
    const sessionID = Date.now();
    const payload = {
      ...segmentStorage.getItem('segmentData', {}),
      session_id: sessionID,
      os: info.get('os'),
      browser: info.get('browser'),
      os_version: info.get('os_version'),
      app_version: info.get('app_version'),
      device: info.get('device'),
      ...properties
    };
    if (validationCheck && process.env.ENVIRONMENT === 'gamma')
      validationForSegmentEvents(payload, eventsKey);
    if (
      categoryStat === evtNameCategoryMap.WITHOUT_CATEGORY ||
      categoryStat === evtNameCategoryMap.BOTH
    )
      sendSegmentEvent(eventName, payload);
    if (
      categoryStat === evtNameCategoryMap.WITH_CATEGORY ||
      categoryStat === evtNameCategoryMap.BOTH
    ) {
      const newEventName = `${eventName} ${payload.goal_uid} Marketing`;
      sendSegmentEvent(newEventName, payload);
    }
  }
};

export const sendGAId = (username, campaign) => (dispatch) => {
  const GAid = window.gaGlobal?.vid;
  if (!GAid) return;
  return dispatch({
    type: 'GOOGLE_ANALYTICS',
    endpoint: `v1/user/${username}/google_analytics_details/`,
    method: 'POST',
    data: {
      ga_info: {
        client_id: window.gaGlobal?.vid,
        context: {
          campaign
        }
      }
    }
  });
};

export default analyticsAction;
